import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  MessageSquare, Send, Search, Users, Circle, RefreshCw, Loader2
} from 'lucide-react';
import { usersAPI, chatInternoAPI } from '@/lib/api';

const Chat = () => {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [conversas, setConversas] = useState([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const messagesEndRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  // Carregar usuários e conversas ao montar
  useEffect(() => {
    if (user?.id) {
      carregarUsuarios();
      carregarConversas();
      
      // Atualizar conversas a cada 15 segundos (só com a aba visível)
      refreshIntervalRef.current = setInterval(() => {
        if (!document.hidden) carregarConversas();
      }, 15000);
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [user?.id, user?.perfil]);

  // Auto scroll para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  // Carregar mensagens quando selecionar usuário
  useEffect(() => {
    if (usuarioSelecionado) {
      setLoading(true);
      carregarMensagens(usuarioSelecionado.id).finally(() => setLoading(false));
      
      // Atualizar mensagens a cada 5 segundos quando conversa aberta (só com a aba visível)
      const msgInterval = setInterval(() => {
        if (!document.hidden) carregarMensagens(usuarioSelecionado.id);
      }, 5000);
      
      return () => clearInterval(msgInterval);
    }
  }, [usuarioSelecionado]);

  const carregarUsuarios = async () => {
    try {
      const response = await usersAPI.listForChat();
      
      // Filtrar para não mostrar o próprio usuário e inativos
      let outrosUsuarios = response.data.filter(u => u.id !== user?.id && u.ativo !== false);
      
      // VENDEDOR: vê apenas BKO e ADMIN
      if (user?.perfil === 'VENDEDOR') {
        outrosUsuarios = outrosUsuarios.filter(u => u.perfil === 'BKO' || u.perfil === 'ADMIN');
      }
      // BKO: vê apenas VENDEDORES e ADMIN
      else if (user?.perfil === 'BKO') {
        outrosUsuarios = outrosUsuarios.filter(u => u.perfil === 'VENDEDOR' || u.perfil === 'ADMIN');
      }
      // ADMIN: vê todos
      
      setUsuarios(outrosUsuarios);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const carregarConversas = async () => {
    try {
      const response = await chatInternoAPI.listarConversas();
      setConversas(response.data);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    }
  };

  const carregarMensagens = async (destinatarioId) => {
    try {
      const response = await chatInternoAPI.obterMensagens(destinatarioId);
      setMensagens(response.data);
      // Atualizar lista de conversas para refletir mensagens lidas
      carregarConversas();
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
  };

  // Enviar mensagem
  const enviarMensagem = async () => {
    if (!novaMensagem.trim() || !usuarioSelecionado || enviando) return;

    setEnviando(true);
    try {
      await chatInternoAPI.enviarMensagem({
        destinatario_id: usuarioSelecionado.id,
        texto: novaMensagem.trim()
      });
      
      setNovaMensagem('');
      // Recarregar mensagens e conversas
      await carregarMensagens(usuarioSelecionado.id);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  // Obter cor do avatar baseado no perfil
  const getCorPerfil = (perfil) => {
    switch (perfil) {
      case 'ADMIN': return 'bg-red-500';
      case 'BKO': return 'bg-blue-500';
      case 'VENDEDOR': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Obter iniciais do nome
  const getIniciais = (nome) => {
    if (!nome) return '??';
    const partes = nome.split(' ');
    if (partes.length >= 2) {
      return (partes[0][0] + partes[1][0]).toUpperCase();
    }
    return nome.substring(0, 2).toUpperCase();
  };

  // Filtrar usuários
  const usuariosFiltrados = usuarios.filter(u => 
    u.nome.toLowerCase().includes(busca.toLowerCase()) ||
    u.email.toLowerCase().includes(busca.toLowerCase()) ||
    u.perfil.toLowerCase().includes(busca.toLowerCase())
  );

  // Obter dados da conversa com um usuário
  const getConversaUsuario = (usuarioId) => {
    return conversas.find(c => 
      c.outro_usuario_id === usuarioId
    );
  };

  // Selecionar usuário para conversa
  const selecionarUsuario = (usuario) => {
    // Se já está selecionado, não fazer nada
    if (usuarioSelecionado && usuarioSelecionado.id === usuario.id) {
      return;
    }
    setUsuarioSelecionado(usuario);
  };

  // Formatar hora
  const formatarHora = (timestamp) => {
    if (!timestamp) return '';
    const data = new Date(timestamp);
    return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Formatar data da última mensagem
  const formatarDataUltimaMensagem = (timestamp) => {
    if (!timestamp) return '';
    const data = new Date(timestamp);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);

    if (data.toDateString() === hoje.toDateString()) {
      return formatarHora(timestamp);
    } else if (data.toDateString() === ontem.toDateString()) {
      return 'Ontem';
    } else {
      return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };

  // Ordenar usuários: conversas existentes primeiro, depois por nome
  const usuariosOrdenados = [...usuariosFiltrados].sort((a, b) => {
    const convA = getConversaUsuario(a.id);
    const convB = getConversaUsuario(b.id);
    
    if (convA && !convB) return -1;
    if (!convA && convB) return 1;
    if (convA && convB) {
      // Ordenar por última mensagem
      const timeA = convA.ultima_mensagem_timestamp ? new Date(convA.ultima_mensagem_timestamp) : new Date(0);
      const timeB = convB.ultima_mensagem_timestamp ? new Date(convB.ultima_mensagem_timestamp) : new Date(0);
      return timeB - timeA;
    }
    return a.nome.localeCompare(b.nome);
  });

  return (
    <div className="h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chat Interno</h1>
            <p className="text-gray-500 text-sm">Comunicação entre equipe</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Circle className="h-3 w-3 fill-green-500 text-green-500" />
          Conectado como <span className="font-semibold">{user?.nome}</span>
          <span className={`px-2 py-0.5 rounded text-xs text-white ${getCorPerfil(user?.perfil)}`}>
            {user?.perfil}
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { carregarUsuarios(); carregarConversas(); }}
            className="ml-2"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Container Principal */}
      <Card className="h-full overflow-hidden">
        <div className="flex h-full">
          {/* Lista de Usuários */}
          <div className="w-80 border-r flex flex-col bg-white">
            {/* Header da Lista */}
            <div className="p-3 bg-gray-100 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar usuário..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-9 bg-white text-sm"
                />
              </div>
            </div>

            {/* Lista de Usuários */}
            <div className="flex-1 overflow-y-auto">
              {usuariosOrdenados.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Nenhum usuário encontrado</p>
                </div>
              ) : (
                usuariosOrdenados.map((usuario) => {
                  const conversa = getConversaUsuario(usuario.id);
                  const naoLidas = conversa?.mensagens_nao_lidas || 0;
                  
                  return (
                    <div
                      key={usuario.id}
                      onClick={() => selecionarUsuario(usuario)}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-100 border-b transition-colors ${
                        usuarioSelecionado?.id === usuario.id ? 'bg-red-50 border-l-4 border-l-red-600' : ''
                      }`}
                    >
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-12 h-12 ${getCorPerfil(usuario.perfil)} rounded-full flex items-center justify-center text-white font-bold`}>
                          {getIniciais(usuario.nome)}
                        </div>
                        {naoLidas > 0 && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {naoLidas > 9 ? '9+' : naoLidas}
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`font-semibold text-gray-900 truncate ${naoLidas > 0 ? 'font-bold' : ''}`}>
                            {usuario.nome}
                          </p>
                          <span className="text-xs text-gray-500">
                            {conversa ? formatarDataUltimaMensagem(conversa.ultima_mensagem_timestamp) : ''}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className={`text-xs truncate ${naoLidas > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                            {conversa?.ultima_mensagem || usuario.email}
                          </p>
                          <span className={`px-1.5 py-0.5 rounded text-xs text-white ${getCorPerfil(usuario.perfil)}`}>
                            {usuario.perfil}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Área do Chat */}
          <div className="flex-1 flex flex-col bg-gray-50">
            {usuarioSelecionado ? (
              <>
                {/* Header do Chat */}
                <div className="bg-red-600 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${getCorPerfil(usuarioSelecionado.perfil)} rounded-full flex items-center justify-center text-white font-bold border-2 border-white`}>
                      {getIniciais(usuarioSelecionado.nome)}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{usuarioSelecionado.nome}</p>
                      <p className="text-xs text-red-100">
                        {usuarioSelecionado.perfil} • {usuarioSelecionado.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mensagens */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-8 w-8 animate-spin text-red-600" />
                    </div>
                  ) : mensagens.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <div className="text-center">
                        <MessageSquare className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                        <p>Nenhuma mensagem ainda</p>
                        <p className="text-sm">Inicie a conversa com {usuarioSelecionado.nome}</p>
                      </div>
                    </div>
                  ) : (
                    mensagens.map((msg) => {
                      const isMinhaMsg = msg.remetente_id === user?.id;
                      
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isMinhaMsg ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl shadow-sm ${
                              isMinhaMsg
                                ? 'bg-red-600 text-white rounded-br-md'
                                : 'bg-white text-gray-800 rounded-bl-md'
                            }`}
                          >
                            {!isMinhaMsg && (
                              <p className="text-xs font-semibold text-red-600 mb-1">
                                {msg.remetente_nome}
                              </p>
                            )}
                            <p className="break-words">{msg.texto}</p>
                            <p className={`text-xs mt-1 ${isMinhaMsg ? 'text-red-100' : 'text-gray-400'} text-right`}>
                              {formatarHora(msg.timestamp)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input de Mensagem */}
                <div className="bg-white border-t px-4 py-3 mb-8 flex items-center gap-3">
                  <Input
                    placeholder="Digite sua mensagem..."
                    value={novaMensagem}
                    onChange={(e) => setNovaMensagem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && enviarMensagem()}
                    className="flex-1"
                    disabled={enviando}
                  />
                  <Button 
                    onClick={enviarMensagem}
                    disabled={!novaMensagem.trim() || enviando}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {enviando ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              </>
            ) : (
              /* Tela de boas-vindas */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="w-12 h-12 text-red-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">Chat Interno</h2>
                  <p className="text-gray-500 max-w-sm">
                    Selecione um usuário à esquerda para iniciar uma conversa.
                  </p>
                  <div className="mt-6 flex justify-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      <span>Admin</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                      <span>BKO</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <span>Vendedor</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Chat;
